// TelcoTrack — Věcná břemena: pravidla výběru varianty vzoru (předmět × zápis v KN × nástroj × zmocněnec).

import { normalizeBremenaCharOsKod } from '../../constants/bremena.constants';
import {
  bremenaTemplateFamilyForInstrument,
  getBremenaTemplateVariant,
  isBremenaTemplateGeneratable,
  listBremenaTemplateVariants,
} from './template-catalog';
import type {
  BremenaObjectFacts,
  BremenaObjectKind,
  BremenaRepresentativeId,
  BremenaTemplateCatalogEntry,
  BremenaTemplateSelectionContext,
  BremenaTemplateSelectionResult,
} from './template-catalog.types';

/** CharOsKod SVJ (společenství vlastníků jednotek) → předmět „jednotka“. */
const CHAROS_SVJ = '6';

function hasText(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

/** Jedna parcela nese budovu (součást pozemku nebo stavba na parcele). */
export function objectFactsHaveBuilding(facts: BremenaObjectFacts): boolean {
  if (facts.source === 'budova' || facts.source === 'both') return true;
  return hasText(facts.soucastiStavba) || hasText(facts.stavbaNaParcele);
}

/** Jedna parcela je dotčena trasou (koridor) — tj. zatížen je i pozemek. */
export function objectFactsHaveLand(facts: BremenaObjectFacts): boolean {
  if (facts.source === 'trasa' || facts.source === 'both') return true;
  if (facts.source === 'budova') return false;
  // Neznámý zdroj: parcela bez budovy je pozemek.
  return !objectFactsHaveBuilding(facts);
}

/**
 * Odvození předmětu smlouvy z faktů o parcelách a smluvních stranách.
 * - SVJ mezi vlastníky → `jednotka`;
 * - pozemky i budovy → `pozemek_budova`;
 * - jen budovy → `budova`;
 * - jinak (včetně prázdného pokrytí) → `pozemek`.
 */
export function deriveBremenaObjectKind(
  objects: readonly BremenaObjectFacts[] | null | undefined,
  charOsKody?: readonly string[] | null,
): BremenaObjectKind {
  const kody = (charOsKody ?? []).map((k) => normalizeBremenaCharOsKod(k)).filter(Boolean);
  if (kody.includes(CHAROS_SVJ)) return 'jednotka';

  const list = objects ?? [];
  const hasBuilding = list.some(objectFactsHaveBuilding);
  const hasLand = list.some(objectFactsHaveLand);
  if (hasBuilding && hasLand) return 'pozemek_budova';
  if (hasBuilding) return 'budova';
  return 'pozemek';
}

export function deriveBremenaNezapsanoVKn(
  objects: readonly BremenaObjectFacts[] | null | undefined,
  explicit?: boolean | null,
): boolean {
  if (typeof explicit === 'boolean') return explicit;
  return (objects ?? []).some((o) => o.nezapsanoVKn === true);
}

/** Vzory mají „pozemek+budova“ pro každou kombinaci s jednotkami; jednotka bez vlastního vzoru spadá do 2.3/3.3/4.3. */
function catalogKindFor(kind: BremenaObjectKind): BremenaObjectKind {
  return kind === 'jednotka' ? 'pozemek_budova' : kind;
}

function pickByRule(
  instrumentType: string | null | undefined,
  objectKind: BremenaObjectKind,
  zapsanoVKn: boolean,
): BremenaTemplateCatalogEntry | null {
  const family = bremenaTemplateFamilyForInstrument(instrumentType);
  if (!family) return null;
  const candidates = listBremenaTemplateVariants({ family, instrumentType, generatableOnly: true });
  if (!zapsanoVKn) {
    const nezapsane = candidates.find((c) => !c.zapsanoVKn);
    if (nezapsane) return nezapsane;
  }
  const wanted = catalogKindFor(objectKind);
  return candidates.find((c) => c.zapsanoVKn && c.objectKind === wanted) ?? null;
}

/**
 * Výběr varianty vzoru. Přepis (`variantOverride`) vítězí, pokud patří k nástroji a je generovatelný;
 * jinak se použije pravidlo a přepis se ohlásí ve `warnings`.
 */
export function resolveBremenaTemplateVariant(ctx: BremenaTemplateSelectionContext): BremenaTemplateSelectionResult {
  const warnings: string[] = [];
  const instrumentType = (ctx.instrumentType ?? '').trim();
  const derivedKind = deriveBremenaObjectKind(ctx.objects, ctx.charOsKody);
  const objectKind = ctx.objectKindOverride ?? derivedKind;
  const zapsanoVKn = !deriveBremenaNezapsanoVKn(ctx.objects, ctx.nezapsanoVKn);
  const representative: BremenaRepresentativeId = ctx.representative ?? 'protelco';

  const override = getBremenaTemplateVariant(ctx.variantOverride);
  if (ctx.variantOverride && !override) {
    warnings.push(`Neznámá varianta vzoru „${ctx.variantOverride}“ — použito automatické pravidlo.`);
  }
  if (override) {
    const compatible = (override.instrumentTypes as readonly string[]).includes(instrumentType);
    if (compatible && isBremenaTemplateGeneratable(override)) {
      return { variant: override, objectKind, zapsanoVKn: override.zapsanoVKn, representative, reason: 'override', warnings };
    }
    warnings.push(
      compatible
        ? `Varianta „${override.code}“ (${override.labelCs}) není generovatelná — použito automatické pravidlo.`
        : `Varianta „${override.code}“ nepatří k nástroji „${instrumentType}“ — použito automatické pravidlo.`,
    );
  }

  const variant = pickByRule(instrumentType, objectKind, zapsanoVKn);
  return {
    variant,
    objectKind,
    zapsanoVKn,
    representative,
    reason: variant ? 'rule' : 'no_template',
    warnings,
  };
}

/** Varianta protokolu (příloha SUZ) odpovídající předmětu SUZ. */
export function resolveBremenaSuzProtokolVariant(objectKind: BremenaObjectKind): BremenaTemplateCatalogEntry | null {
  const wanted = catalogKindFor(objectKind);
  return listBremenaTemplateVariants({ family: 'suz_protokol' }).find((e) => e.objectKind === wanted) ?? null;
}
